import { Field } from '@atlaskit/form';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import Toggle from '@atlaskit/toggle';
import { getCompactSelectStyles } from '@databases/atlaskit/compact-select-styles';
import Select, { type StylesConfig } from '@databases/atlaskit/Select';
import { messages as intlMessages } from '@databases/intl/messages';
import { TimeFormat } from '@databases/model/field';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { type DatabaseField, type DatabaseFieldType } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import React, { type FunctionComponent, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { messages } from '../messages.ts';

type TimeFormatOption = {
  label: string;
  value: TimeFormat;
};

type DateFieldSettingsProps = {
  contentId: string;
  field: DatabaseField<DatabaseFieldType.DATE>;
};

const toggleWrapperStyles = xcss({
  marginInline: 'space.negative.075'
});

const includeTimeToggleRowStyles = xcss({
  alignItems: 'center',
  width: '100%',
  marginBlock: 'space.negative.050'
});

const timeFormatSelectStyles: StylesConfig<TimeFormatOption, false> = {
  ...getCompactSelectStyles<TimeFormatOption, false>(),
  menuPortal: (base) => ({
    ...base,
    zIndex: 750
  })
};

const DateFieldSettings: FunctionComponent<DateFieldSettingsProps> = ({
  contentId,
  field
}) => {
  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();
  const includeTime = Boolean(field.settings.timeFormat);
  const includeTimeLabel = formatMessage(
    intlMessages['field-settings.type-date-include-time']
  );
  const timeFormatLabel = formatMessage(messages.dateTimeFormatLabel);
  const menuPortalTarget =
    typeof globalThis.document === 'undefined' ? undefined : globalThis.document.body;
  const timeFormatOptions = useMemo<TimeFormatOption[]>(
    () => [
      {
        label: formatMessage(messages.dateTimeFormat12Hour),
        value: TimeFormat.TWELVE_HOUR
      },
      {
        label: formatMessage(messages.dateTimeFormat24Hour),
        value: TimeFormat.TWENTY_FOUR_HOUR
      }
    ],
    [formatMessage]
  );

  const handleTimeFormatChange = useCallback(
    (timeFormat: TimeFormat | undefined) => {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_DATE_FIELD_TIME_FORMAT,
          fieldId: field.id,
          timeFormat
        })
      );
    },
    [contentId, dispatch, field.id]
  );

  const handleIncludeTimeChange = useCallback(() => {
    handleTimeFormatChange(includeTime ? undefined : TimeFormat.TWELVE_HOUR);
  }, [handleTimeFormatChange, includeTime]);

  const handleSelectChange = useCallback(
    (option: TimeFormatOption | null) => {
      if (option) {
        handleTimeFormatChange(option.value);
      }
    },
    [handleTimeFormatChange]
  );

  return (
    <Stack space="space.050">
      <Field
        label={
          <Text size="small" weight="bold" color="color.text.subtle">
            {includeTimeLabel}
          </Text>
        }
        name="date-include-time-field"
      >
        {() => (
          <Inline
            alignBlock="center"
            spread="space-between"
            xcss={includeTimeToggleRowStyles}
          >
            <Text color="color.text.subtle">
              <label htmlFor="date-include-time-toggle">{includeTimeLabel}</label>
            </Text>
            <Box xcss={toggleWrapperStyles}>
              <Toggle
                id="date-include-time-toggle"
                onChange={handleIncludeTimeChange}
                isChecked={includeTime}
              />
            </Box>
          </Inline>
        )}
      </Field>

      {includeTime && (
        <Field
          label={
            <Text size="small" weight="bold" color="color.text.subtle">
              {timeFormatLabel}
            </Text>
          }
          name="date-time-format-field"
        >
          {({ fieldProps }) => (
            <Select<TimeFormatOption>
              inputId={fieldProps.id}
              ariaLabel={timeFormatLabel}
              menuPortalTarget={menuPortalTarget}
              menuPosition="fixed"
              onChange={handleSelectChange}
              options={timeFormatOptions}
              spacing="compact"
              styles={timeFormatSelectStyles}
              value={timeFormatOptions.find(
                (option) => option.value === field.settings.timeFormat
              )}
            />
          )}
        </Field>
      )}
    </Stack>
  );
};

export default DateFieldSettings;
